import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CONTINENTS, getContinent } from "@/data/destinations";
import { useLang } from "@/i18n/LanguageContext";
import DotMap from "@/components/DotMap";
import Reveal from "@/components/Reveal";

export default function Destinations() {
  const { t } = useLang();
  const [continent, setContinent] = useState("europe");
  const [country, setCountry] = useState("france");

  const active = getContinent(continent);
  const selectedCountry = active.countries.find((c) => c.slug === country) || active.countries[0];

  const pickContinent = (id) => {
    setContinent(id);
    const c = getContinent(id);
    if (c) setCountry(c.countries[0].slug);
  };

  return (
    <section id="destinations" className="relative overflow-hidden py-28 lg:py-40" data-testid="destinations-section">
      <div className="absolute inset-0">
        {CONTINENTS.flatMap((c) => c.countries).map((c) => (
          <img
            key={c.slug}
            src={c.img}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${selectedCountry.slug === c.slug ? "opacity-100" : "opacity-0"}`}
            loading="lazy"
          />
        ))}
        <div className="absolute inset-0 bg-[#050814]/[0.93]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050814] via-transparent to-[#050814]" />
      </div>

      <div className="relative mx-auto max-w-[1500px] px-6 lg:px-12">
        <Reveal>
          <p className="eyebrow mb-6">{t("atlas.eyebrow")}</p>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <h2 className="max-w-xl font-serif text-4xl font-light leading-tight text-white sm:text-5xl lg:text-6xl">
              {t("atlas.title")}
            </h2>
            <p className="max-w-sm text-sm font-light leading-relaxed text-white/55 md:text-base">
              {t("atlas.sub")}
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-2" data-testid="continent-tabs">
            {CONTINENTS.map((c) => (
              <button
                key={c.id}
                data-testid={`continent-tab-${c.id}`}
                onClick={() => pickContinent(c.id)}
                className={`border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300 ${continent === c.id ? "border-[#d4af37] bg-[#d4af37]/10 text-[#e8cf7a]" : "border-white/15 text-white/55 hover:border-white/40 hover:text-white"}`}
              >
                {t(`cont.${c.id}`)}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-10">
          <DotMap
            activeContinent={continent}
            selected={selectedCountry.slug}
            onSelectCountry={(c) => { setCountry(c.slug); setContinent(c.continent); }}
            onSelectContinent={pickContinent}
          />
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {active.countries.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.08}>
              <Link
                to={`/destinations/${c.slug}`}
                data-testid={`country-card-${c.slug}`}
                className={`group relative block overflow-hidden border transition-colors duration-500 ${selectedCountry.slug === c.slug ? "border-[#d4af37]/60" : "border-white/10 hover:border-white/30"}`}
                onMouseEnter={() => setCountry(c.slug)}
              >
                <div className="relative h-64 overflow-hidden">
                  <img src={c.img} alt={c.name} loading="lazy" className="img-spotlight h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050814] via-[#050814]/20 to-transparent" />
                </div>
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
                  <div>
                    <div className="font-serif text-2xl font-light text-white">{c.name}</div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#e8cf7a]/80">
                      {t("country.citiesCount", { count: c.cities.length })}
                    </div>
                  </div>
                  <span className="flex h-9 w-9 items-center justify-center border border-[#d4af37]/40 text-[#d4af37] transition-all duration-300 group-hover:bg-[#d4af37] group-hover:text-[#050814]">
                    <ArrowUpRight size={15} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
